import { initialMetricsPublisher } from '../metrics';
import {
  WeeklySpprSummary,
  WeeklySqprSummary
} from '../types/backendService';
import { UserDetails } from '../context/GlobalContext';
import moment from 'moment';

interface SessionMetrics {
  userId: string;
  warehouseId: string;
  timestamp: number;
  sessionElapsedSeconds?: number;
  percentile?: number;
  rank?: number;
  rankPopulation?: number;
  actualDPMO?: number;
  expectedDPMO?: number;
  performance?: number;
}

interface CompetenciesMetrics {
  userId: string;
  warehouseId: string;
  timestamp: number;
  coreCompetency: string;
  percentile?: number;
  rank?: number;
  rankPopulation?: number;
  actualDPMO?: number;
  expectedDPMO?: number;
  performance?: number;
}

interface FAQMetrics {
  userId: string;
  warehouseId: string;
  timestamp: number;
  faq: string;
}

interface HelpMeMetrics {
  userId: string;
  warehouseId: string;
  timestamp: number;
  optionClicked: string;
  actionCreated: boolean;
}

const metricsPublisher = initialMetricsPublisher.newChildActionPublisherForMethod('Metric');

// Safety measure to ensure that session metrics don't continue to get emitted in unexpected situations
// where a user's session open for a long time.
const MAX_SESSION_ELAPSED_SECONDS = 600; // 10 minutes

class MetricSender {
  public generateCompetenciesMetrics = (
      userDetails: UserDetails,
      spprSummaries: WeeklySpprSummary[]|undefined,
      sqprSummaries: WeeklySqprSummary[]|undefined,
      coreCompetency: string) => {
    const spprSummary: WeeklySpprSummary|undefined = MetricSender.determineMostRecentPeriod(spprSummaries || []);
    const sqprSummary: WeeklySqprSummary|undefined = MetricSender.determineMostRecentPeriod(sqprSummaries || []);

    const competenciesMetrics: CompetenciesMetrics = {
      userId: userDetails.login,
      warehouseId: userDetails.warehouseId,
      timestamp: moment().unix(),
      coreCompetency,
      percentile: MetricSender.roundNumber(spprSummary?.rankPercentile, 2),
      rank: spprSummary?.rank,
      rankPopulation: spprSummary?.totalPopulation,
      actualDPMO: MetricSender.roundNumber(sqprSummary?.currentDPMO, 0),
      expectedDPMO: MetricSender.roundNumber(sqprSummary?.expectedDPMO, 0),
      performance: MetricSender.roundNumber(sqprSummary?.performance, 2),
    };

    /**
     * The userDetails.login != '' check is to prevent metrics from being sent if a user is not logged in
     * such as would otherwise occur if the page is closed on the "Login" page.
     */
    if (userDetails.login != '' && !userDetails.isAdmin) {
      metricsPublisher.publishString("Competency", JSON.stringify(competenciesMetrics));
    }
  };

  public generateSessionMetrics = (
      userDetails: UserDetails,
      spprSummaries: WeeklySpprSummary[]|undefined,
      sqprSummaries: WeeklySqprSummary[]|undefined,
      sessionStartTime: moment.Moment) => {
    const spprSummary: WeeklySpprSummary|undefined = MetricSender.determineMostRecentPeriod(spprSummaries || []);
    const sqprSummary: WeeklySqprSummary|undefined = MetricSender.determineMostRecentPeriod(sqprSummaries || []);

    const sessionElapsedSeconds = moment().unix() - sessionStartTime.unix();

    const sessionMetrics: SessionMetrics = {
      userId: userDetails.login,
      warehouseId: userDetails.warehouseId,
      timestamp: sessionStartTime.unix(),
      sessionElapsedSeconds,
      percentile: MetricSender.roundNumber(spprSummary?.rankPercentile, 2),
      rank: spprSummary?.rank,
      rankPopulation: spprSummary?.totalPopulation,
      actualDPMO: MetricSender.roundNumber(sqprSummary?.currentDPMO, 0),
      expectedDPMO: MetricSender.roundNumber(sqprSummary?.expectedDPMO, 0),
      performance: MetricSender.roundNumber(sqprSummary?.performance, 2),
    };

    if (userDetails.login != '' && !userDetails.isAdmin && sessionElapsedSeconds <= MAX_SESSION_ELAPSED_SECONDS) {
      metricsPublisher.publishString("Session", JSON.stringify(sessionMetrics));
    }
  };

  public generateFAQMetrics = (userDetails: UserDetails, faq: string) => {
    const faqMetrics: FAQMetrics = {
      userId: userDetails.login,
      warehouseId: userDetails.warehouseId,
      timestamp: moment().unix(),
      faq,
    };

    if (userDetails.login != '' && !userDetails.isAdmin) {
      metricsPublisher.publishString("FAQ", JSON.stringify(faqMetrics));
    }
  };

  public generateHelpMeMetrics = (userDetails: UserDetails, optionClicked: string, actionCreated: boolean) => {
    const helpMeMetrics: HelpMeMetrics = {
      userId: userDetails.login,
      warehouseId: userDetails.warehouseId,
      timestamp: moment().unix(),
      optionClicked,
      actionCreated,
    };

    if (userDetails.login != '' && !userDetails.isAdmin) {
      metricsPublisher.publishString("HelpMe", JSON.stringify(helpMeMetrics));
    }
  };

  private static roundNumber(aNumber: number|undefined, digits: number = 0){
    if (aNumber == undefined) {
      return undefined;
    } else {
      const rounded = Math.pow(10, digits);
      return Math.round(aNumber * rounded) / rounded;
    }
  }

  /**
   * Due to SPPR/SQPR not updating at a guaranteed time it is difficult to determine
   * if the user has no data for the most recent SPPR/SQPR period of it the data
   * has not yet been generated. Therefore we take the most recent period to be the latest
   * period that has data in the last 8 days.
   */
  private static determineMostRecentPeriod = (periods: any[]) => {
    let latestPeriod: any = null;

    const currentMoment = moment();
    const thresholdMoment = currentMoment.subtract(8, 'days');

    for (const period of periods) {
      if ((period.endTime! > thresholdMoment.unix())
          && (latestPeriod == null || period.endTime! > latestPeriod.endTime!)) {
        latestPeriod = period;
      }
    }

    return latestPeriod;
  }
};

export const metricSender = new MetricSender();
